import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import LocationTile from "./LocationTile";
import { UserContext } from "../UserContext";
import api from "../api/index";
import "./styles/Marketplace.css";

const LISTINGS_STORAGE_KEY = "marketplace_listings_v1";

const buildAddress = (row) => {
    const line1 = row.address || "";
    const line2Parts = [
        row.city,
        row.state && row.zipcode ? `${row.state} ${row.zipcode}` : row.state || "",
        row.country,
    ].filter(Boolean);
    const line2 = line2Parts.join(", ");
    return [line1, line2].filter(Boolean).join(", ");
};

const addDaysIso = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString();
};

export default function Marketplace() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();

    const [sellOverlayOpen, setSellOverlayOpen] = useState(false);
    const [sellType, setSellType] = useState(null); // "auction" | "flat"
    const [sellLocations, setSellLocations] = useState([]);
    const [sellLoading, setSellLoading] = useState(false);
    const [sellError, setSellError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [pricingModalOpen, setPricingModalOpen] = useState(false);
    const [flatPrice, setFlatPrice] = useState("");
    const [auctionMinPrice, setAuctionMinPrice] = useState("");
    const [auctionBasePrice, setAuctionBasePrice] = useState("");

    const tiles = Array.from({ length: 9 }, (_, index) => ({
        name: `Location ${index + 1}`,
        address: `${120 + index} Market St, San Francisco, CA`,
        price: 10000 + index * 250,
        highestBid: 20000 + index * 225,
        isAuction: index % 3 === 0,
    }));

    useEffect(() => {
        if (!sellType || !user?.id || !user?.access_token) return;

        const load = async () => {
            setSellLoading(true);
            setSellError("");

            try {
                const res = await fetch(
                    `${api.main}api/agent/all_locations?id=${user.id}&access_token=${user.access_token}`
                );
                if (!res.ok) {
                    throw new Error("Unable to load locations.");
                }
                const data = await res.json();
                const formatted = (data.data || []).map((row) => {
                    const address = buildAddress(row);
                    return {
                        ...row,
                        address_display: address,
                        key: String(row.registration_id ?? row.location_id ?? row.id ?? ""),
                    };
                });

                const activeLicensed = formatted.filter(
                    (row) => row.status === "A" && row.license_id
                );

                setSellLocations(activeLicensed);
            } catch (err) {
                setSellError(err.message || "Unable to load locations.");
            } finally {
                setSellLoading(false);
            }
        };

        load();
    }, [sellType, user?.id, user?.access_token]);

    const searchedLocations = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return sellLocations;
        const tokens = q.split(/\s+/).filter(Boolean);
        return sellLocations.filter((row) => {
            const hay = [
                row.business_name,
                row.address_display,
                row.city,
                row.state,
                row.zipcode,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return tokens.every((token) => hay.includes(token));
        });
    }, [sellLocations, searchQuery]);

    const openSellOverlay = () => setSellOverlayOpen(true);
    const closeSellOverlay = () => setSellOverlayOpen(false);

    const handleChooseType = (type) => {
        setSellType(type);
        setSellOverlayOpen(false);
        setSelectedLocation(null);
        setPricingModalOpen(false);
        setFlatPrice("");
        setAuctionMinPrice("");
        setAuctionBasePrice("");
    };

    const handleResetSell = () => {
        setSellType(null);
        setSelectedLocation(null);
        setPricingModalOpen(false);
        setFlatPrice("");
        setAuctionMinPrice("");
        setAuctionBasePrice("");
    };

    const handleRowSelect = (_, selectedRows) => {
        const row = selectedRows[0];
        if (!row) return;
        setSelectedLocation(row);
        setPricingModalOpen(true);
    };

    const canSell =
        sellType === "flat"
            ? Number(flatPrice) > 0
            : Number(auctionMinPrice) > 0 && Number(auctionBasePrice) > 0;

    const handleConfirmSell = () => {
        if (!selectedLocation || !sellType) return;

        const listing = {
            id: `${selectedLocation.key}-${Date.now()}`,
            locationId: selectedLocation.key,
            name: selectedLocation.business_name || "Location",
            address: selectedLocation.address_display || buildAddress(selectedLocation),
            type: sellType,
            flatPrice: sellType === "flat" ? Number(flatPrice) : null,
            minPrice: sellType === "auction" ? Number(auctionMinPrice) : null,
            basePrice: sellType === "auction" ? Number(auctionBasePrice) : null,
            createdAt: new Date().toISOString(),
            expiresAt: sellType === "flat" ? addDaysIso(30) : addDaysIso(5),
        };

        const existing = JSON.parse(localStorage.getItem(LISTINGS_STORAGE_KEY) || "[]");
        localStorage.setItem(
            LISTINGS_STORAGE_KEY,
            JSON.stringify([listing, ...existing])
        );

        setPricingModalOpen(false);
        setSelectedLocation(null);
        handleResetSell();
        navigate("/my-listed-licenses");
    };

    return (
        <div className="marketplace-page">
            <div className="page-header">
                <div>
                    <p className="page-eyebrow">Marketplace</p>
                    <h2>License Marketplace</h2>
                    <p className="section-subtitle">Place to buy and sell licenses.</p>
                </div>
                <div className="marketplace-header-actions">
                    <button className="btn btn-primary" onClick={openSellOverlay}>
                        Sell License
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate("/my-listed-licenses")}
                    >
                        My Listed Licenses
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {sellOverlayOpen && (
                    <motion.div
                        className="marketplace-sell-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeSellOverlay}
                    >
                        <motion.div
                            className="marketplace-sell-modal"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="marketplace-sell-header">
                                <h3>Choose a sale type</h3>
                                <p>Pick how you want to list your license.</p>
                            </div>
                            <div className="marketplace-sell-options">
                                <motion.button
                                    type="button"
                                    className="marketplace-sell-option"
                                    onClick={() => handleChooseType("auction")}
                                >
                                    <span className="marketplace-sell-option-title">Auction</span>
                                    <span className="marketplace-sell-option-copy">
                                        Accept bids and sell in 5 days or sooner.
                                    </span>
                                </motion.button>
                                <motion.button
                                    type="button"
                                    className="marketplace-sell-option"
                                    onClick={() => handleChooseType("flat")}
                                >
                                    <span className="marketplace-sell-option-title">Flat Cost</span>
                                    <span className="marketplace-sell-option-copy">
                                        Set a fixed price with a 30-day listing.
                                    </span>
                                </motion.button>
                            </div>
                            <div className="marketplace-sell-actions">
                                <button className="btn btn-secondary" onClick={closeSellOverlay}>
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {sellType && (
                <section className="page-section marketplace-sell-panel">
                    <div className="section-heading">
                        <div>
                            <p className="section-eyebrow">Sell</p>
                            <h3>Select a location</h3>
                        </div>
                        <span className="section-subtitle">
                            Showing active locations with licenses.
                        </span>
                    </div>
                    <div className="marketplace-sell-toolbar">
                        <div className="marketplace-sell-search">
                            <label htmlFor="marketplace-search">Search locations</label>
                            <input
                                id="marketplace-search"
                                type="text"
                                placeholder="Search by name or address"
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                            />
                        </div>
                        <div className="marketplace-sell-toolbar-actions">
                            <button className="btn btn-secondary" onClick={handleResetSell}>
                                Start over
                            </button>
                        </div>
                    </div>
                    {sellError && <p className="text-muted">{sellError}</p>}
                    <div className="marketplace-sell-table">
                        <table className="marketplace-sell-table-grid">
                            <thead>
                                <tr>
                                    <th>Select</th>
                                    <th>Location</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sellLoading && (
                                    <tr>
                                        <td colSpan={4} className="marketplace-sell-empty">
                                            Loading locations...
                                        </td>
                                    </tr>
                                )}
                                {!sellLoading && searchedLocations.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="marketplace-sell-empty">
                                            No active licensed locations found.
                                        </td>
                                    </tr>
                                )}
                                {!sellLoading &&
                                    searchedLocations.map((row) => (
                                        <tr key={row.key}>
                                            <td>
                                                <input
                                                    type="radio"
                                                    name="marketplace-location"
                                                    checked={selectedLocation?.key === row.key}
                                                    onChange={() =>
                                                        handleRowSelect(null, [row])
                                                    }
                                                />
                                            </td>
                                            <td>{row.business_name || "Location"}</td>
                                            <td>{row.address_display}</td>
                                            <td>{row.status === "A" ? "Active" : "Inactive"}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <section className="page-section marketplace-panel">
                <div className="section-heading">
                    <div>
                        <p className="section-eyebrow">Browse</p>
                        <h3>Available Listings</h3>
                    </div>
                    <span className="section-subtitle">{tiles.length} locations ready for bids</span>
                </div>

                <div className="marketplace-grid">
                    {tiles.map((tile, index) => (
                        <LocationTile
                            key={`${tile.name}-${index}`}
                            name={tile.name}
                            address={tile.address}
                            price={tile.price}
                            highestBid={tile.highestBid}
                            isAuction={tile.isAuction}
                        />
                    ))}
                </div>
            </section>

            <Modal
                open={pricingModalOpen}
                onCancel={() => setPricingModalOpen(false)}
                footer={null}
                title={sellType === "auction" ? "Auction details" : "Flat cost details"}
            >
                {sellType === "flat" ? (
                    <div className="marketplace-modal-content">
                        <label htmlFor="flat-price">Flat cost</label>
                        <input
                            id="flat-price"
                            type="number"
                            min="0"
                            placeholder="Enter price"
                            value={flatPrice}
                            onChange={(event) => setFlatPrice(event.target.value)}
                        />
                        <p className="marketplace-sell-warning">
                            This listing will be removed in 30 days.
                        </p>
                    </div>
                ) : (
                    <div className="marketplace-modal-content">
                        <label htmlFor="auction-min">Minimum cost (hidden)</label>
                        <input
                            id="auction-min"
                            type="number"
                            min="0"
                            placeholder="Enter minimum cost"
                            value={auctionMinPrice}
                            onChange={(event) => setAuctionMinPrice(event.target.value)}
                        />
                        <label htmlFor="auction-base">Base ask price</label>
                        <input
                            id="auction-base"
                            type="number"
                            min="0"
                            placeholder="Enter base ask price"
                            value={auctionBasePrice}
                            onChange={(event) => setAuctionBasePrice(event.target.value)}
                        />
                        <p className="marketplace-sell-warning">
                            The location will be on auction for 5 days or until
                            someone buys it immediately.
                        </p>
                    </div>
                )}
                <div className="marketplace-modal-actions">
                    <button className="btn btn-secondary" onClick={() => setPricingModalOpen(false)}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleConfirmSell}
                        disabled={!canSell}
                    >
                        Sell
                    </button>
                </div>
            </Modal>
        </div>
    );
}
